import React, { useState } from 'react';
import {
  useMachineDisplay,
  formatUsdtRangeFromXt,
} from '../../hooks/useMachineDisplay';
import { useXtPrice } from '../../hooks/useXtPrice';
import { useNotify } from '../../context/NotificationContext';
import { TIER_LABELS } from '../../utils/constants';
import { Spinner } from '../common/Loader';

/**
 * Admin panel for per-tier XT display ranges.
 *
 * Values are stored server-side in `/data/machine-display.json` via the
 * PHP endpoint at `/api/machine-display.php`. When you click "Publish to
 * All Users" the current values are pushed to the server so every visitor
 * sees them on next load. Local edits still take effect in this browser
 * immediately (before publishing).
 */
export default function MachineDisplayPanel() {
  const { config, updateTier, resetDefaults, publishToServer } = useMachineDisplay();
  const { xtPrice } = useXtPrice();
  const notify = useNotify();

  const [busy, setBusy] = useState(false);

  function save(idx, patch) {
    updateTier(idx, patch);
  }

  async function publish() {
    setBusy(true);
    try {
      await publishToServer();
      notify.success('Published to server — all users will see these values on their next visit.');
    } catch (err) {
      notify.error(err.message || 'Publish failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="admin-panel">
      <h3>Machine Display Ranges</h3>
      <p className="panel-hint">
        Configure the <strong>XT tokens/day range</strong> shown on machine cards (e.g. "Earns 1.00 to 2.00 XT tokens per day").
        This is a <strong>frontend display value only</strong> — the actual mining payout is determined by the on-chain
        <code style={{ margin: '0 4px', color: 'var(--accent)' }}>dailyRewardUSDT</code>
        set in the Package Tiers tab.
      </p>

      <div style={{
        padding: 12, background: 'rgba(0, 229, 160, 0.06)', border: '1px solid rgba(0, 229, 160, 0.25)',
        borderRadius: 8, marginBottom: 16, fontSize: 12, color: 'var(--text-muted)',
      }}>
        ℹ Edits below apply in this browser immediately. To apply the values to <strong>all users</strong>,
        click <strong>Publish to All Users</strong>. The values are stored on the
        server in <code>/data/machine-display.json</code>.
      </div>

      {config.map((c, i) => (
        <div key={i} className="tier-editor" style={{ marginTop: 10 }}>
          <div className="tier-editor-header">
            <div className="name">{TIER_LABELS[i]}</div>
          </div>
          <div className="tier-fields">
            <div>
              <label className="small-label">Min XT / day</label>
              <input
                className="small-input"
                value={c.minXt}
                onChange={(e) => save(i, { minXt: e.target.value.replace(/[^0-9.]/g, '') })}
              />
            </div>
            <div>
              <label className="small-label">Max XT / day</label>
              <input
                className="small-input"
                value={c.maxXt}
                onChange={(e) => save(i, { maxXt: e.target.value.replace(/[^0-9.]/g, '') })}
              />
            </div>
            <div style={{ minWidth: 220, color: 'var(--text-muted)', fontSize: 12, fontFamily: 'var(--font-mono)' }}>
              Preview: <strong style={{ color: 'var(--accent)' }}>{c.minXt} to {c.maxXt} XT / day</strong>
              <div style={{ fontSize: 11, marginTop: 2 }}>
                ≈ <strong style={{ color: 'var(--accent)' }}>{formatUsdtRangeFromXt(i, config, xtPrice)} USDT / day</strong>
              </div>
            </div>
          </div>
        </div>
      ))}

      <div className="divider" />

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <button
          className="btn btn-primary"
          disabled={busy}
          onClick={publish}
        >
          {busy ? <><Spinner /> Publishing...</> : 'Publish to All Users'}
        </button>

        <button
          className="btn btn-secondary"
          onClick={() => {
            resetDefaults();
            notify.success('Reset to defaults (locally). Click Publish to push these to all users.');
          }}
        >
          Reset to Defaults
        </button>
      </div>
    </div>
  );
}
